// Firestore への読み書き（個人情報・取引先の情報）。守りは firebase/firestore.rules
import { Timestamp, deleteDoc, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { driverPlaceId } from '@torapon/shared';
import type { DriverDoc, NoteDoc, PlaceDoc, RollCallDetailDoc, RoutineDoc, StaffDoc, TemplateDoc } from '@torapon/shared';
import { fsdb } from './firebase';

export const putDriver = (id: string, d: DriverDoc) => setDoc(doc(fsdb(), 'drivers', id), d);
export const putStaff = (id: string, d: StaffDoc) => setDoc(doc(fsdb(), 'staff', id), d);
export const putPlace = (id: string, d: PlaceDoc) => setDoc(doc(fsdb(), 'places', id), d);
/** 名前・電話・メールの変更。アドレスを変えたら、ひもづけ（uid）を外す（仕様書「変えると端末の紐づけも解除」） */
export const updatePerson = (kind: 'drivers' | 'staff', id: string, d: Record<string, unknown>) => updateDoc(doc(fsdb(), kind, id), d);
/** アドレスを変えたら、前のアドレスに送った招待コードを使えなくする */
export const clearInvite = (officeId: string, driverId: string) => setDoc(doc(fsdb(), 'inviteHeads', driverId), { officeId, hash: '' });
/** 退職：この時刻からログインできない。null で再雇用 */
export const setRetireAt = (kind: 'drivers' | 'staff', id: string, iso: string | null) =>
  updateDoc(doc(fsdb(), kind, id), { retireAt: iso ? Timestamp.fromDate(new Date(iso)) : null, active: true });
export const putTemplate = (id: string, d: TemplateDoc) => setDoc(doc(fsdb(), 'templates', id), d);
export const deleteTemplateDoc = (id: string) => deleteDoc(doc(fsdb(), 'templates', id));
/** 配送を送るとき：このドライバーに行き先の場所を見せる印を付ける（仕様書「driverPlaces」） */
// 定期便の担当の印（keep）は残したまま書く
export const showPlacesToDriver = (officeId: string, driverId: string, placeIds: string[]) =>
  Promise.all([...new Set(placeIds)].map((placeId) => setDoc(doc(fsdb(), 'driverPlaces', driverPlaceId(driverId, placeId)), { officeId, driverId, placeId }, { merge: true })));
/** 定期便の担当に、その行き先を見せ続ける印を付ける（定期便は事務所の操作なしに届くため）。keep=false で外す */
export const keepPlacesForDriver = (officeId: string, driverId: string, placeIds: string[], keep: boolean) =>
  Promise.all([...new Set(placeIds)].map((placeId) => setDoc(doc(fsdb(), 'driverPlaces', driverPlaceId(driverId, placeId)), { officeId, driverId, placeId, keep }, { merge: true })));
export const putRoutineName = (id: string, d: RoutineDoc) => setDoc(doc(fsdb(), 'routines', id), d);
/** メモの本文。なければ空 */
export const readNote = async (id: string) => ((await getDoc(doc(fsdb(), 'notes', id))).data() as NoteDoc | undefined)?.text ?? '';
export const putNote = (id: string, d: NoteDoc) => setDoc(doc(fsdb(), 'notes', id), d);
/** 定期便・予約のメモの ID（便を送る前に付けるメモ。配送アプリは便 → 予定 → 定期便の順に引く） */
export const scheduleNoteId = (scheduleId: string) => `sch_${scheduleId}`;
export const routineNoteId = (routineId: string) => `rtn_${routineId}`;
/** メモの宛先のドライバーを付け替える（担当が変わったとき。前の担当は読めなくなる）。ないメモは何もしない */
export async function rebindNotes(ids: string[], driverId: string | null) {
  await Promise.all(ids.map(async (id) => {
    const d = (await getDoc(doc(fsdb(), 'notes', id))).data() as NoteDoc | undefined;
    if (!d || (d.driverId ?? null) === driverId) return;
    const { driverId: _old, ...rest } = d;
    await setDoc(doc(fsdb(), 'notes', id), { ...rest, ...(driverId ? { driverId } : {}) });
  }));
}

/** 点呼の中身。見られない役割（配車スタッフ）や、まだないものは null */
export async function rollCallDetails(ids: string[]): Promise<Map<string, RollCallDetailDoc>> {
  const got = await Promise.all(ids.map(async (id) => {
    try { const s = await getDoc(doc(fsdb(), 'rollCallDetails', id)); return s.exists() ? [id, s.data() as RollCallDetailDoc] as const : null; } catch { return null; }
  }));
  return new Map(got.filter((x): x is readonly [string, RollCallDetailDoc] => !!x));
}
export const confirmRollCallDetail = (id: string, d: Pick<RollCallDetailDoc, 'healthOk' | 'healthNote' | 'inspectionOk' | 'instruction'>) =>
  updateDoc(doc(fsdb(), 'rollCallDetails', id), Object.fromEntries(Object.entries(d).filter(([, v]) => v !== undefined)));
